/**
 * Toast Notification System
 * Beautiful, accessible toast notifications for user feedback
 */

import { create } from 'zustand';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { useEffect } from 'react';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
    id: string;
    type: ToastType;
    title: string;
    message?: string;
    duration?: number;
}

interface ToastState {
    toasts: Toast[];
    addToast: (toast: Omit<Toast, 'id'>) => void;
    removeToast: (id: string) => void;
    clearAll: () => void;
}

export const useToastStore = create<ToastState>((set) => ({
    toasts: [],
    
    addToast: (toast) => {
        const id = Math.random().toString(36).substring(7);
        const newToast = { ...toast, id };
        
        set((state) => ({
            toasts: [...state.toasts, newToast],
        }));
        
        // Auto-remove after duration
        const duration = toast.duration || 5000;
        setTimeout(() => {
            set((state) => ({
                toasts: state.toasts.filter((t) => t.id !== id),
            }));
        }, duration);
    },
    
    removeToast: (id) => {
        set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
        }));
    },
    
    clearAll: () => {
        set({ toasts: [] });
    },
}));

// Toast Container Component
export function ToastContainer() {
    const { toasts, removeToast } = useToastStore();
    
    return (
        <div className="fixed top-4 right-4 z-50 space-y-3 max-w-md">
            {toasts.map((toast) => (
                <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
            ))}
        </div>
    );
}

// Individual Toast Component
function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
    const icons = {
        success: CheckCircle,
        error: AlertCircle,
        warning: AlertTriangle,
        info: Info,
    };
    
    const colors = {
        success: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300',
        error: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300',
        warning: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300',
        info: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300',
    };
    
    const iconColors = {
        success: 'text-green-600 dark:text-green-400',
        error: 'text-red-600 dark:text-red-400',
        warning: 'text-yellow-600 dark:text-yellow-400',
        info: 'text-blue-600 dark:text-blue-400',
    };
    
    const Icon = icons[toast.type];
    
    return (
        <div
            className={`${colors[toast.type]} border rounded-lg shadow-lg p-4 flex items-start gap-3 animate-slide-in-right`}
            role="alert"
        >
            <Icon className={`w-5 h-5 ${iconColors[toast.type]} flex-shrink-0 mt-0.5`} />
            
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{toast.title}</p>
                {toast.message && (
                    <p className="text-sm mt-1 opacity-90">{toast.message}</p>
                )}
            </div>
            
            <button
                onClick={onClose}
                className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
                aria-label="Close notification"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}

// Helper hooks for common toast patterns
export function useToast() {
    const addToast = useToastStore((state) => state.addToast);
    
    return {
        success: (title: string, message?: string, duration?: number) => {
            addToast({ type: 'success', title, message, duration });
        },
        
        error: (title: string, message?: string, duration?: number) => {
            addToast({ type: 'error', title, message, duration });
        },
        
        warning: (title: string, message?: string, duration?: number) => {
            addToast({ type: 'warning', title, message, duration });
        },
        
        info: (title: string, message?: string, duration?: number) => {
            addToast({ type: 'info', title, message, duration });
        },
        
        promise: async <T,>(
            promise: Promise<T>,
            {
                loading,
                success,
                error,
            }: {
                loading: string;
                success: string | ((data: T) => string);
                error: string | ((error: any) => string);
            }
        ): Promise<T> => {
            const loadingToastId = Math.random().toString(36);
            addToast({ type: 'info', title: loading, duration: 0 });
            
            try {
                const data = await promise;
                useToastStore.getState().clearAll();
                addToast({
                    type: 'success',
                    title: typeof success === 'function' ? success(data) : success,
                });
                return data;
            } catch (err) {
                useToastStore.getState().clearAll();
                addToast({
                    type: 'error',
                    title: typeof error === 'function' ? error(err) : error,
                });
                throw err;
            }
        },
    };
}

// Add to your global CSS
const toastStyles = `
@keyframes slide-in-right {
    from {
        transform: translateX(100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

.animate-slide-in-right {
    animation: slide-in-right 0.3s ease-out;
}
`;
